import { useMemo } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import type { InstrumentProfitabilityItemDto } from '@/api/types/riskDashboard';

type R = InstrumentProfitabilityItemDto;
const n2 = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2));

export function InstrumentProfitabilityTable({ rows, loading }: { rows: R[]; loading: boolean }) {
  // Legacy default order: Total PnL desc.
  const data = useMemo(() => [...rows].sort((a, b) => (b.totalPnl ?? 0) - (a.totalPnl ?? 0)), [rows]);

  const byPip = (v: number | null | undefined, pipPlace: number | null | undefined) =>
    v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, pipPlace ? Math.round(pipPlace) : 5);

  const columns: DataGridColumn<R>[] = [
    { key: 'symbol', title: 'Instrument', className: 'text-start', value: (r) => r.symbol },
    { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
    { key: 'stpBBook', title: 'STP Bbook', className: 'text-center', value: (r) => r.stpBBook },
    { key: 'totalPnl', title: 'Total PnL', className: 'text-end', value: (r) => r.totalPnl, cell: (r) => n2(r.totalPnl) },
    { key: 'averagePnl', title: 'Avg PnL', className: 'text-end', value: (r) => r.averagePnl, cell: (r) => n2(r.averagePnl) },
    { key: 'averageInPips', title: 'Avg in Pips', className: 'text-end', value: (r) => r.averageInPips, cell: (r) => n2(r.averageInPips) },
    { key: 'weightedAveragePips', title: 'Weighted Average Pips', className: 'text-end', value: (r) => r.weightedAveragePips, cell: (r) => n2(r.weightedAveragePips) },
    { key: 'averageDifferenceInPrice', title: 'Average Difference in price', className: 'text-end', value: (r) => r.averageDifferenceInPrice, cell: (r) => byPip(r.averageDifferenceInPrice, r.pipPlace) },
    { key: 'maxPnl', title: 'Max PnL', className: 'text-end', value: (r) => r.maxPnl, cell: (r) => n2(r.maxPnl) },
    { key: 'maxInPips', title: 'Max in Pips', className: 'text-end', value: (r) => r.maxInPips, cell: (r) => n2(r.maxInPips) },
    { key: 'minPnl', title: 'Min PnL', className: 'text-end', value: (r) => r.minPnl, cell: (r) => n2(r.minPnl) },
    { key: 'minInPips', title: 'Min in Pips', className: 'text-end', value: (r) => r.minInPips, cell: (r) => n2(r.minInPips) },
    { key: 'averageProfit', title: 'Average Profit', className: 'text-end', value: (r) => r.averageProfit, cell: (r) => n2(r.averageProfit) },
    { key: 'averageProfitInPips', title: 'Average Profit in Pips', className: 'text-end', value: (r) => r.averageProfitInPips, cell: (r) => n2(r.averageProfitInPips) },
    { key: 'numberOfProfitTrades', title: 'Number of Profit Trades', className: 'text-end', value: (r) => r.numberOfProfitTrades },
    { key: 'averageLoss', title: 'Average Loss', className: 'text-end', value: (r) => r.averageLoss, cell: (r) => n2(r.averageLoss) },
    { key: 'averageLossInPips', title: 'Average Loss in Pips', className: 'text-end', value: (r) => r.averageLossInPips, cell: (r) => n2(r.averageLossInPips) },
    { key: 'numberOfLossTrades', title: 'Number of Loss Trades', className: 'text-end', value: (r) => r.numberOfLossTrades },
    { key: 'riskReward', title: 'Risk Reward', className: 'text-center', value: (r) => r.riskReward },
    { key: 'averageDays', title: 'Avg Days', className: 'text-end', value: (r) => r.averageDays, cell: (r) => n2(r.averageDays) },
    { key: 'maxDays', title: 'Max Days', className: 'text-end', value: (r) => r.maxDays, cell: (r) => n2(r.maxDays) },
    { key: 'minDays', title: 'Min Days', className: 'text-end', value: (r) => r.minDays, cell: (r) => n2(r.minDays) },
  ];

  return (
    <DataGrid
      columns={columns}
      data={loading ? [] : data}
      rowKey={(r) => `${r.symbol}:${r.connector}`}
      pageSize={5}
      pageSizeOptions={[5, 10, 25, 50, 100]}
      card
      includeExcel
      excelFileName="Instrument Profitability"
      emptyMessage={loading ? 'Loading…' : 'No data for the current filters.'}
    />
  );
}
